import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';

import { ModalService } from '../../core/services/modal.service';
import { BooksService } from '../../core/services/data/books.service';
import { LibraryService } from '../../core/services/data/library.service';

import { LibraryAPIResponse, RaceBookAPIResponse } from '../../types/responses';
import { ILibraryBook, RetailerClassifications } from '../../types/datatypes';
import { AMAZON_HOST, AMAZON_WWW_HOST } from './constants';

export type UniversalLinksErrorCodes = 'NO_DATA_REJECT' | 'NOOK_REJECT' | 'OTHER_REJECT';

@Component({
	selector: 'app-universal-book-fetch',
	templateUrl: './universal-book-fetch.component.html',
	styleUrls: [
		'./universal-book-fetch.component.scss',
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class UniversalBookFetchComponent {
	@Input() set link(value: null | string) {
		if (!value) return;
		this.url = value;
		this.lastFetchedUrl = value;
	}

	@Output() library: EventEmitter<{ library: ILibraryBook; linkClassification: RetailerClassifications }> = new EventEmitter();

	@Output() v2: EventEmitter<{ response: RaceBookAPIResponse; target: { ASIN: string; url: string }}> = new EventEmitter();

	@Output() tbd: EventEmitter<boolean> = new EventEmitter();

	// IF an error occurs we can return the url and the classification. For now we will still allow the user
	// to continue with the link. But, there may be cases in the future where we dont allow this. For instance,
	// if the link returned 404 - We may not allow that, as its most likely just adding work to the OPs team.
	// If an error should not be returned, we will not emit with this.
	@Output() errorResult: EventEmitter<{ url: string; linkClassification: RetailerClassifications }> = new EventEmitter();

	@Output() status: EventEmitter<'in-progress' | 'idle'> = new EventEmitter();

	public url = '';

	public errorOptions = {
		title: 'Unknown Error',
		text: [`Hmm, that's not supposed to happen. An unknown error occurred.
      Please try again with a different link, or wait a minute and try again.`],
		actions: [{
			type: 'solid',
			text: 'Try again',
			callback: () => this.modalService.close('universal-link-error'),
		}],
	};

	// eslint-disable-next-line max-len
	public tooltipText = 'You can enter any approved retailer in this field to use as your primary link. Approved retailers are Amazon, Apple, Google, Kobo, and Barnes and Noble (Nook).';

	private lastFetchedUrl = null;

	constructor(
		private booksService: BooksService,
		private libraryService: LibraryService,
		private modalService: ModalService,
	) { }

	async fetch() {
		if (this.lastFetchedUrl === this.url) {
			return;
		}

		this.inProgress();

		this.lastFetchedUrl = this.url;

		if (!URL.canParse(this.url)) {
			this.handleInvalidUrl();
			return;
		}

		const url = new URL(this.url);

		if (url.host === AMAZON_HOST || url.host === AMAZON_WWW_HOST) {
			this.v2Fetch(url.href);
			return;
		}

		this.libraryService.fetch(url.href).subscribe({
			next: (result: LibraryAPIResponse & { linkClassification: RetailerClassifications; code: UniversalLinksErrorCodes }) => {
				if (result.success) {
					// Based on the conversation around
					// https://docs.google.com/document/d/1A66kUl5go2GYmMkxgv-0Pe-9UfwV2AyQIBG4xmg7yqM/edit?tab=t.0#heading=h.l8vttxa1jsrg
					// For phase 1 we will bail out of any unrecognized retailers. We need to get reader buying to the "other"
					// retailers, before we advertise to them. This is preventing expensive scrapes for those links.
					if (result.code) {
						this.idle();
						this.handleByCode(result.code, url.href, result.linkClassification);
						return;
					}
					// END

					this.handleFetchSuccess(result.library, result.linkClassification);
				} else {
					this.handleFetchFailure(new Error(result.message || 'Unknown Error'));
				}
				this.idle();
			},
			error: (error) => {
				this.handleFetchFailure(error);
				this.idle();
			},
		});
	}

	async v2Fetch(url: string) {
		const ASIN = this.booksService.getASIN(url);

		this.booksService.getBookData(ASIN).subscribe({
			next: (result: RaceBookAPIResponse) => {
				/**
				 * Pass the whole thing back so we don't have to mess with the legacy handler too much.
				 * The parent component can handle errors itself.
				 */
				this.v2.emit({ response: result, target: { ASIN, url } });
				this.idle();
			},
			error: (err) => {
				this.handleFetchFailure(err);
				this.idle();
			},
		});
	}

	handleFetchSuccess(library: ILibraryBook, linkClassification?) {
		this.library.emit({
			library,
			linkClassification,
		});
	}

	handleFetchFailure(err: Error) {
		// TODO: get failure states from Ferol.

		this.errorOptions = {
			title: 'Unknown Error',
			text: [`Hmm, that's not supposed to happen. An unknown error occurred.
        Please try again with a different link, or wait a minute and try again.`],
			actions: [{
				type: 'solid',
				text: 'Try again',
				callback: () => this.modalService.close('universal-link-error'),
			}],
		};

		this.modalService.open('universal-link-error');
	}

	handleInvalidUrl() {
		this.tbd.emit(true);
		this.idle();
	}

	inProgress() {
		this.status.emit('in-progress');
	}

	idle() {
		this.status.emit('idle');
	}

	private handleByCode(code: UniversalLinksErrorCodes, url: string, linkClassification: RetailerClassifications) {
		if (code === 'OTHER_REJECT') {
			this.handleInvalidUrl();
			return;
		}
		switch (code) {
			case 'NO_DATA_REJECT':
			case 'NOOK_REJECT':
				this.errorOptions = {
					title: 'Unable to find book information.',
					text: [`Sorry, we couldn't find any book information. The website you entered may have hidden the data from us.
            You can still use this link if you'd like. We just wont be able to grab any of the book information for you.`],
					actions: [{
						type: 'solid',
						text: 'Ok',
						callback: () => {
							this.errorResult.emit({ linkClassification, url });
							this.closeError();
						},
					}],
				};
				break;
			default:
				this.errorOptions = {
					title: 'Unknown Error',
					text: [`Hmm, that's not supposed to happen. An unknown error occurred.
            Please try again with a different link, or wait a minute and try again.
            You can still use this link if you'd like. We just wont be able to grab any of the book information for you.`],
					actions: [{
						type: 'solid',
						text: 'Try again',
						callback: () => this.closeError(),
					}],
				};
		}

		this.modalService.open('universal-link-error');
	}

	private closeError() {
		this.modalService.close('universal-link-error');
	}
}
